import React from "react";
import { getHeroSlug, heroSrcSet, DEFAULT_HERO_SIZES } from "@/lib/heroImages";

/**
 * PictureImg - drop-in <img> replacement that emits <picture> with AVIF+WebP
 * responsive sources when the src has known variants in /assets/hero/*.
 *
 * Falls back to a plain <img> when the src is not mapped. Never changes layout.
 *
 * Props:
 *   src (required) - original image path (e.g. "/assets/coche-...webp")
 *   alt (required)
 *   width, height - intrinsic dimensions (recommended to prevent CLS)
 *   sizes - responsive sizes hint (defaults to DEFAULT_HERO_SIZES)
 *   priority - if true, sets fetchpriority=high + loading=eager (LCP candidate)
 *   className - forwarded to the <img>
 *   imgProps - any extra props for the underlying <img>
 */
export default function PictureImg({
  src,
  alt,
  width,
  height,
  sizes = DEFAULT_HERO_SIZES,
  priority = false,
  className,
  onError,
  style,
  ...imgProps
}) {
  const slug = getHeroSlug(src);
  const img = (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchpriority={priority ? "high" : "auto"}
      className={className}
      style={style}
      onError={onError}
      {...imgProps}
    />
  );

  if (!slug) return img;

  return (
    <picture>
      <source type="image/avif" srcSet={heroSrcSet(slug, "avif")} sizes={sizes} />
      <source type="image/webp" srcSet={heroSrcSet(slug, "webp")} sizes={sizes} />
      {img}
    </picture>
  );
}
